/**
 * Stale-bundle detection and self-healing.
 *
 * WHY THIS EXISTS (incident 2026-08-21, Gareth Simpkins / HMS-078):
 * prod deployed a new frontend at 2026-08-20 07:55 while his browser was still
 * running the bundle it had loaded on 08-19. His session survived on a refresh
 * cookie, so he never fetched fresh HTML — old JS kept talking to a backend that
 * had moved 22 commits underneath it. The first changed contract threw, and with
 * no error boundary the whole React tree unmounted: a blank page. Reloading
 * re-used the cached `index.html`, so every retry reproduced it exactly. That
 * "retrying changes nothing" is the signature of this failure.
 *
 * The cure is a version handshake: the running bundle's filename is compared to
 * the one the server is serving right now. A mismatch means this tab is older
 * than the deployment and must reload itself — exactly once.
 *
 * Deliberately implemented against `asset-manifest.json` rather than a build id
 * baked in at compile time: it needs no change to the CI/CD pipeline, and the
 * manifest is generated by CRA on every build, so it cannot drift out of sync
 * with the bundle it describes.
 */

const MANIFEST_URL = '/asset-manifest.json';
const RELOAD_GUARD_KEY = 'hms.staleBundleReloadedAt';

/** Statuses returned by checkBundleFreshness(). */
export const FRESH = 'fresh';
export const STALE = 'stale';
/** Manifest missing/unreadable (CRA dev server, offline, nginx misconfig). */
export const UNKNOWN = 'unknown';

/**
 * The main bundle this tab is actually executing, e.g. "/static/js/main.0f96051a.js".
 *
 * Read from the DOM rather than `document.currentScript` because that is null
 * once the script has finished executing, and this check runs after boot.
 */
export function getRunningMainBundle() {
  try {
    const scripts = Array.from(document.querySelectorAll('script[src]'));
    const main = scripts
      .map((s) => s.getAttribute('src') || '')
      .find((src) => /\/static\/js\/main\.[^/]+\.js$/.test(src));
    if (!main) return null;
    // Normalise to a path so an absolute src and a relative manifest entry compare equal.
    return new URL(main, window.location.origin).pathname;
  } catch (e) {
    return null;
  }
}

/**
 * The main bundle the server is serving right now.
 *
 * `cache: 'no-store'` is load-bearing: the entire failure mode is a browser
 * serving stale cached copies, so a cached manifest would agree with the stale
 * bundle and the check would pass while the app is broken.
 */
export async function fetchDeployedMainBundle() {
  try {
    const res = await fetch(MANIFEST_URL, { cache: 'no-store', credentials: 'same-origin' });
    if (!res || !res.ok) return null;
    const manifest = await res.json();
    const main = manifest && manifest.files && manifest.files['main.js'];
    if (typeof main !== 'string' || !main) return null;
    return new URL(main, window.location.origin).pathname;
  } catch (e) {
    return null;
  }
}

/**
 * Compare what we are running against what is deployed.
 *
 * Returns UNKNOWN — never STALE — whenever either side cannot be determined.
 * A false "fresh" costs one crashed screen that the error boundary still
 * contains; a false "stale" would put every user into a reload they don't need.
 */
export async function checkBundleFreshness() {
  const running = getRunningMainBundle();
  if (!running) return UNKNOWN;

  const deployed = await fetchDeployedMainBundle();
  if (!deployed) return UNKNOWN;

  return running === deployed ? FRESH : STALE;
}

/** True once this tab has already spent its single automatic reload. */
export function hasAlreadyReloaded() {
  try {
    return !!window.sessionStorage.getItem(RELOAD_GUARD_KEY);
  } catch (e) {
    // Private modes can throw on sessionStorage. Treat as "already reloaded" so
    // a browser we cannot put a guard in can never be pushed into a reload loop.
    return true;
  }
}

/**
 * Reload this tab onto the deployed bundle — at most once per tab.
 *
 * PREMISE: sessionStorage is per-tab and survives a reload, so the guard is
 * still set when the page comes back. If this ever moves to localStorage or a
 * cookie, re-check that a second genuine deploy in the same session can still heal.
 *
 * Returns true if a reload was triggered, false if the guard blocked it — in
 * which case the caller should surface the error card instead of reloading again.
 */
export function reloadOntoFreshBundle() {
  if (hasAlreadyReloaded()) return false;
  try {
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch (e) {
    return false; // Cannot guard => must not reload.
  }
  // `location.reload()` can be served from cache by some browsers; replacing the
  // URL with a cache-busting param forces a real request for fresh HTML.
  const url = new URL(window.location.href);
  url.searchParams.set('_v', String(Date.now()));
  window.location.replace(url.toString());
  return true;
}

/**
 * Full guard: if this tab is running an out-of-date bundle, heal it once.
 * Safe to call on boot and again when something has just thrown.
 *
 * Never throws — a fault in the freshness check must not become the outage.
 */
export async function healIfStale() {
  try {
    const status = await checkBundleFreshness();
    if (status !== STALE) return status;
    return reloadOntoFreshBundle() ? 'reloading' : 'stale-after-reload';
  } catch (e) {
    return UNKNOWN;
  }
}
